<template>
  <div>
    <div
      ref="betslipContainer"
      class="betslip"
    >
      <div class="betslip-header">
        <h3>{{ $t('betslip.title') }}</h3>
        <div class="header-line" />
      </div>

      <div
        v-if="showBookABetModule && !promotionStore.isAppliedOnSlip"
        class="betslip-load-booking"
      >
        <div class="booking-search-container">
          <input
            v-model="bookingCodeInput"
            type="text"
            inputmode="text"
            data-keyboard="alphanumeric"
            class="booking-search-input"
            :placeholder="$t('betslip.enterBookingCode')"
            @keyup.enter="loadBookedBet"
          >
          <button
            type="button"
            class="booking-search-button"
            :disabled="!bookingCodeInput.trim() || isLoadingBooking"
            @click="loadBookedBet"
          >
            {{ isLoadingBooking ? $t('betslip.loading') : $t('betslip.load') }}
          </button>
        </div>
        <div
          v-if="bookingMessage"
          class="booking-message success"
        >
          {{ bookingMessage }}
        </div>
        <div
          v-if="bookingErrorMessage"
          class="booking-message error"
        >
          {{ bookingErrorMessage }}
        </div>
      </div>

      <div
        v-if="bets.length === 0"
        class="betslip-empty-container"
      >
        <div
          v-if="bookBetSuccessData"
          ref="bookedBetSuccessRef"
          class="booked-bet-success-inline"
          tabindex="-1"
        >
          <div class="booking-header">
            <span class="check-icon">✓</span>
            <span class="booking-title">{{ $t('betslip.betBookedSuccess') }}</span>
          </div>
          
          <div class="booking-code-box">
            <span class="booking-code">{{ bookBetSuccessData.booking_code }}</span>
          </div>

          <div
            v-if="bookBetSuccessData.barcode"
            class="barcode-box"
          >
            <img
              :src="`data:image/png;base64,${bookBetSuccessData.barcode}`"
              alt="Barcode"
              class="barcode-img"
            >
          </div>

          <div class="booking-info">
            <div class="info-row">
              <span>{{ $t('betslip.totalAmount') }}</span>
              <span>{{ formatCurrency(bookBetSuccessData.total_bet_amount || 0, unref(displayCurrency)) }}</span>
            </div>
            <div class="info-row">
              <span>{{ $t('betslip.expires') }}</span>
              <span class="expires-text">{{ formatDate(bookBetSuccessData.expires_at || '') }}</span>
            </div>
          </div>

          <div class="booking-actions">
            <button
              class="download-btn"
              @click="downloadBookedBarcode"
            >
              {{ $t('betslip.downloadReceipt') }}
            </button>
            <button
              class="done-btn"
              @click="bookBetSuccessData = null"
            >
              {{ $t('common.done') }}
            </button>
          </div>
        </div>
        <div
          v-else-if="showSuccess"
          ref="betslipSuccessRef"
          class="success-message"
          tabindex="-1"
        >
          <div class="success-content">
            <span class="success-icon">🎉</span>
            <div class="success-text">
              <h3>{{ $t('betslip.betPlacedSuccess') }}</h3>
              <p>{{ $t('betslip.goodLuck') }}</p>
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-state"
        >
          <p>{{ $t('betslip.empty') }}</p>
        </div>
        <div
          v-if="isAuthenticated && !bookBetSuccessData"
          class="view-last-bet"
        >
          <button
            v-if="lastBetReference"
            class="view-bet-button"
            :disabled="isLoadingLastBet"
            @click="viewLastBet"
          >
            {{ isLoadingLastBet ? $t('betslip.loadingLastBet') : $t('betslip.viewLastBet') }}
          </button>
          <button
            class="view-bet-button"
            @click="viewWagers"
          >
            {{ $t('betslip.viewWagers') }}
          </button>
        </div>
      </div>

      <template v-else>
        <div class="betslip-content">
          <div
            v-for="(bet, index) in bets"
            :key="index"
            class="bet-item"
            :class="{ 'expired-bet': showExpiredBetOptions && hasExpiredDraws(bet) }"
          >
            <div class="bet-item-header">
              <span class="game-name">{{ bet.game_name }}</span>
              <div class="bet-item-actions">
                <span class="bet-odds">{{ bet.odds }}</span>
                <button
                  class="remove-bet-btn"
                  :aria-label="$t('betslip.removeBet')"
                  @click="removeBetAtIndex(index)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="trash-icon-svg"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="bet-item-details">
              <div class="draws-section">
                <div class="draws-header">
                  <span class="draws-label">{{ $t('betslip.draws') }} ({{ bet.draws?.length || 0 }}):</span>
                  <button 
                    v-if="bet.draws && bet.draws.length > 1"
                    class="toggle-draws-btn"
                    :aria-expanded="expandedDraws[index]"
                    @click="toggleDrawsExpanded(index)"
                  >
                    {{ expandedDraws[index] ? $t('common.hide') : $t('common.show') }}
                    <svg 
                      class="chevron-icon" 
                      :class="{ 'rotated': expandedDraws[index] }"
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      stroke-width="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
                <div
                  v-if="bet.draws && bet.draws.length > 0"
                  class="draws-list"
                  :class="{ 'expanded': expandedDraws[index] }"
                >
                  <div 
                    v-for="(draw, drawIndex) in betDrawsForDisplay(bet.draws, index)" 
                    :key="draw.event_id || drawIndex" 
                    class="draw-item"
                    :class="{ 'expired': isDrawExpired(draw) }"
                  >
                    <span class="draw-date">{{ formatDate(draw.draw_date) }}</span>
                    <button 
                      v-if="bet.draws.length > 1"
                      class="remove-draw-btn" 
                      :aria-label="$t('betslip.removeDraw')"
                      @click="removeDrawFromBet(index, drawIndex)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <line
                          x1="18"
                          y1="6"
                          x2="6"
                          y2="18"
                        />
                        <line
                          x1="6"
                          y1="6"
                          x2="18"
                          y2="18"
                        />
                      </svg>
                    </button>
                  </div>
                  <button 
                    v-if="hiddenDrawsCount(bet.draws.length, index) > 0" 
                    class="more-draws-indicator clickable"
                    @click="toggleDrawsExpanded(index)"
                  >
                    +{{ hiddenDrawsCount(bet.draws.length, index) }} {{ $t('betslip.moreDraws') }}
                  </button>
                </div>
              </div>
              <div class="market-name">
                {{ formatMarketDisplay(bet) }}
              </div>
              <div
                v-if="bet.betType && (!useAltNameForMarket || !hasSelectedNumbers(bet.numbers))"
                class="bet-type"
              >
                {{ $t('betslip.betType') }}: {{ bet.betType }}
              </div>
              
              <div
                v-if="bet.numbers && hasSelectedNumbers(bet.numbers)"
                class="numbers-section"
              >
                <div class="numbers-label">
                  {{ $t('betslip.numbersChosen') }}:
                </div>
                <div class="number-circles">
                  <span
                    v-for="number in Array.isArray(bet.numbers) && Array.isArray(bet.numbers[0]) ? bet.numbers[0] : []"
                    :key="'main-' + number"
                    class="number-circle"
                  >
                    {{ number }}
                  </span>
                  <template v-if="Array.isArray(bet.numbers) && Array.isArray(bet.numbers[1]) && bet.numbers[1].length">
                    <span class="plus-sign">+</span>
                    <span
                      v-for="bonus in bet.numbers[1]"
                      :key="'bonus-' + bonus"
                      class="number-circle bonus"
                    >
                      {{ bonus }}
                    </span>
                  </template>
                </div>
              </div>

              <div class="bet-inputs-row">
                <div class="input-fieldset">
                  <div class="input-fieldset-label">
                    <label>{{ $t('betslip.stake') }}</label>
                  </div>
                  <div class="input-wrapper">
                    <span class="currency">{{ unref(displayCurrency) }}</span>
                    <input
                      type="text"
                      inputmode="decimal"
                      data-keyboard="decimal"
                      :value="getStakeDisplayValue(index)"
                      @input="(event) => handleStakeChange(event, index)"
                      @blur="(event) => handleStakeBlur(event, index)"
                      @focus="(event) => handleStakeFocus(event, index)"
                      @keydown="(event) => handleStakeKeydown(event)"
                    >
                  </div>
                </div>
                <div class="input-fieldset readonly">
                  <div class="input-fieldset-label">
                    <label>{{ $t('betslip.toWin') }}</label>
                  </div>
                  <div class="input-wrapper">
                    <span class="currency">{{ unref(displayCurrency) }}</span>
                    <span class="win-value">
                      {{ formatCurrencyValue(lineDisplayPotentials[index] ?? 0) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="betslip-footer">
          <div class="footer-actions">
            <label
              v-if="showPromotionOffer"
              class="promotion-offer-inline"
              :title="$t('betslip.promotionOptionalHint')"
            >
              <input
                type="checkbox"
                class="promotion-offer-checkbox"
                :checked="promotionStore.applyToSlip"
                @change="handlePromotionApplyToggle"
              >
              <span class="promotion-offer-inline-text">
                <span class="promotion-offer-inline-label">{{ $t('betslip.usePromotion') }}</span>
                <span class="promotion-offer-inline-detail">{{ promotionBannerText }}</span>
              </span>
            </label>
            <button
              class="clear-all-btn"
              @click="clearBetslip"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="trash-icon-small"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span>{{ $t('common.clearAll') }}</span>
            </button>
          </div>
          <p
            v-if="showPromotionOffer && promotionStore.applyToSlip && promotionSlipValidationError"
            class="promotion-offer-error"
          >
            {{ promotionSlipValidationError }}
          </p>

          <div class="summary-box">
            <div class="summary-item">
              <span class="summary-label">{{ $t('betslip.totalBetAmount') }}</span>
              <span class="summary-value">{{ formatCurrency(totalStake, unref(displayCurrency)) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ $t('betslip.totalPotentialReturns') }}</span>
              <span class="summary-value">{{ formatCurrency(totalReturns, unref(displayCurrency)) }}</span>
            </div>
          </div>

          <div
            v-if="showExpiredBetOptions"
            class="expired-bet-options"
          >
            <div class="expired-message">
              <span class="warning-icon">⚠️</span>
              <span class="warning-text">{{ $t('betslip.expiredBets') }}</span>
            </div>
            <div class="expired-actions">
              <button
                class="remove-expired-btn"
                @click="removeExpiredBets"
              >
                {{ $t('betslip.remove') }}
              </button>
              <button
                v-if="hasAnyGameWithNextDraw"
                class="move-to-next-btn"
                @click="moveToNextDraw"
              >
                {{ $t('betslip.moveToNext') }}
              </button>
            </div>
          </div>

          <div
            v-if="inlineError"
            class="inline-error-message"
          >
            <span class="error-icon">❌</span>
            <span class="error-text">{{ inlineError }}</span>
          </div>

          <div
            v-if="showBookABetModule && bookBetRestrictionMessage"
            class="book-bet-restriction-message"
          >
            <span
              class="warning-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                class="info-icon-svg"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="7"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
                <path
                  d="M10 8V12"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
                <circle
                  cx="10"
                  cy="5.8"
                  r="1"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span class="warning-text">{{ bookBetRestrictionMessage }}</span>
          </div>

          <div class="betslip-action-buttons">
            <button
              class="bet-now-btn"
              :class="{
                processing: isProcessingBet,
                unauthenticated: !isAuthenticated,
                'payout-max-exceeded': exceedsBetslipPayoutMax && isAuthenticated,
              }"
              :disabled="isAuthenticated ? (isProcessingBet || isEmpty || showExpiredBetOptions || exceedsBetslipPayoutMax) : false"
              @click="handleBetButtonClick"
            >
              <span
                v-if="exceedsBetslipPayoutMax && isAuthenticated"
                class="payout-max-warning"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  class="payout-max-warning-icon"
                  aria-hidden="true"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="7"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />
                  <path
                    d="M10 8V12"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                  <circle
                    cx="10"
                    cy="5.8"
                    r="1"
                    fill="currentColor"
                  />
                </svg>
                <span>{{ betNowPayoutMaxMessage }}</span>
              </span>
              <template v-else>
                {{ !isAuthenticated ? $t('auth.pleaseLogin') : (isProcessingBet ? $t('betslip.pleaseWait') : $t('betslip.betNow')) }}
              </template>
            </button>

            <button
              v-if="showBookABetModule"
              class="book-bet-btn"
              :class="{ processing: isProcessingBookBet }"
              :disabled="isBookBetDisabled"
              @click="handleBookBet"
            >
              {{ isProcessingBookBet ? $t('betslip.pleaseWait') : $t('betslip.bookBet') }}
            </button>
          </div>

          <div class="terms-text">
            {{ $t('betslip.tsAndCs') }}
          </div>

          <button
            class="close-betslip-button"
            @click="closeBetslip"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </template>
    </div>

    <BaseModal
      :is-open="isLastBetModalOpen"
      :title="$t('betslip.lastBetDetails')"
      full-screen
      hide-footer
      flush-body
      @close="isLastBetModalOpen = false"
    >
      <TransactionDetails
        v-if="transactionsStore.currentTransaction"
        :transaction="transactionsStore.currentTransaction"
        :is-rebetting="isRebetting"
        @rebet="handleLastBetRebet"
        @close="isLastBetModalOpen = false"
      />
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  watch,
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  unref,
} from "vue";
import { useBetslipStore } from "../stores/betslip";
import type { BetslipItem } from "../stores/betslip";
import { useToast } from "../composables/useToast";
import { useGameStore } from "../stores/game";
import { useTransactionsStore } from "../stores/transactions";
import { useAuthStore } from "../stores/auth";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { formatCurrency, formatOdds } from '../utils/currency';
import { useBetSettings } from "../composables/useBetSettings";
import {
  calcPotentialWin,
  displayPotentialWin,
  normalizeStakeValue,
  sumPotentialWins,
  toNumber,
} from "../utils/betLimits";
import {
  parseCommittedStakeInput,
  parseStakePreviewAmount,
  sanitizeStakeInputString,
} from "../utils/stakeInput";
import { iframeCommunication } from "../utils/iframeCommunication";
import { usePromotionStore } from "../stores/promotion";
import { promotionBannerLabel } from '../types/promotion'
import { usePromotionSync } from '../composables/usePromotionSync'
import { scrollElementIntoViewAfterRender } from "../utils/scrollToElement";
import BaseModal from "./BaseModal.vue";
import TransactionDetails from "./wagers/TransactionDetails.vue";
import { useRebetFromTransaction } from "../composables/useRebetFromTransaction";

const emit = defineEmits(["close", "selectDraws", "drawsChanged", "clearedAfterSubmission"]);

const betslipContainer = ref<HTMLElement | null>(null);
const bookedBetSuccessRef = ref<HTMLElement | null>(null);
const betslipSuccessRef = ref<HTMLElement | null>(null);

const { t } = useI18n();
const gameStore = useGameStore();
const transactionsStore = useTransactionsStore();
const betslipStore = useBetslipStore();
const authStore = useAuthStore();
const promotionStore = usePromotionStore();
const { refreshPromotions } = usePromotionSync();
const promotionLookupDone = ref(false);
const router = useRouter();
const route = useRoute();
const { removeBet, clearBetslip: storeClearBetslip, updateLastStake } = betslipStore;
const betslip = computed(() => betslipStore.betslip);
const { showToast } = useToast();
const isProcessingBet = ref(false);
const showSuccess = ref(false);
const isUpdatingFromProps = ref(false);
const isClearingBetslip = ref(false);
const localStakeValues = ref<{ [key: number]: string }>({});
const stakeEditingByIndex = ref<Record<number, boolean>>({});
const showExpiredBetOptions = ref(false);
const expandedDraws = ref<{ [key: number]: boolean }>({});
const expiredBets = ref<Array<{ betIndex: number; drawIndex: number; draw: { draw_date: string; cutoff: string; event_id: number } }>>([]);
const bookingCodeInput = ref('');
const isLoadingBooking = ref(false);
const bookingMessage = ref('');
const bookingErrorMessage = ref('');
const currentBookingCode = ref<string | null>(null);
const isProcessingBookBet = ref(false);
const bookBetSuccessData = ref<{
  booking_code: string;
  barcode?: string;
  expires_at?: string;
  total_bet_amount?: number;
  bets?: BetslipItem[];
} | null>(null);

const clearBetslip = (resetSuccess: boolean | MouseEvent = true) => {
  const shouldReset = typeof resetSuccess === 'boolean' ? resetSuccess : true;
  isClearingBetslip.value = true;
  if (shouldReset) {
    showSuccess.value = false;
  }
  storeClearBetslip();
  
  // Notify upstream that betslip was cleared
  iframeCommunication.sendMessage('betslip-cleared');

  setTimeout(() => {
    isClearingBetslip.value = false;
  }, 100);
};

const cleanupTimer = ref<number | null>(null);
const inlineError = ref<string | null>(null);
const isMobile = ref(false);
const openDrawDropdownIndex = ref<number | null>(null);
const clickOutsideHandler = ref<((event: MouseEvent) => void) | null>(null);
const isLastBetModalOpen = ref(false);
const { applyRebetFromCurrentTransaction, isRebetting } = useRebetFromTransaction();
const isLoadingLastBet = ref(false);

const {
  betMin,
  betMax,
  payoutMax,
  payoutMaxPerBetslip,
  betLimit,
  betDefault,
  currencySymbol,
  currencyCode,
  allowDecimals,
} = useBetSettings();

const bets = computed(() => betslip.value || []);

const isNormalizingStakes = ref(false);

const getBetLineMultipliers = (bet: BetslipItem) => {
  const drawCount = Array.isArray(bet.draws) ? bet.draws.length : 1;
  const numBets = typeof bet.numBets === "number" && bet.numBets > 0 ? bet.numBets : 1;
  return { drawCount, numBets };
};

const getStakeNormalizeOptions = () => ({
  betMin: unref(betMin),
  betMax: unref(betMax),
  payoutMax: unref(payoutMaxPerBetslip) ? 0 : unref(payoutMax),
  allowDecimals: unref(allowDecimals),
});

const formatStakeInputValue = (stake: number): string => {
  if (!stake || stake <= 0) return "";
  if (unref(allowDecimals)) {
    return (Math.round(stake * 100) / 100).toString();
  }
  return String(Math.floor(stake));
};

const isStakeFieldEditing = (index: number): boolean =>
  Boolean(stakeEditingByIndex.value[index]);

const getStakeDisplayValue = (index: number): string => {
  if (isStakeFieldEditing(index)) {
    return localStakeValues.value[index] ?? "";
  }
  const stake = bets.value[index]?.stake ?? 0;
  if (stake > 0) {
    return formatStakeInputValue(stake);
  }
  return "";
};

const setRawBetStake = (index: number, stake: number) => {
  if (!bets.value[index]) return;
  isNormalizingStakes.value = true;
  const updatedBets = [...bets.value];
  updatedBets[index] = { ...updatedBets[index], stake: Math.max(0, stake) };
  betslipStore.betslip = updatedBets;
  betslipStore.saveToStorage();
  nextTick(() => {
    isNormalizingStakes.value = false;
  });
};

const effectiveStakeForBet = (index: number): number => {
  if (isStakeFieldEditing(index)) {
    return parseStakePreviewAmount(localStakeValues.value[index] ?? "");
  }
  return bets.value[index]?.stake ?? 0;
};

const betPotentialInputs = computed(() =>
  bets.value.map((bet, index) => {
    const { drawCount, numBets } = getBetLineMultipliers(bet);
    return {
      stake: effectiveStakeForBet(index),
      odds: toNumber(bet.odds),
      drawCount,
      numBets,
    };
  })
);

const lineDisplayPotentials = computed(() => {
  const inputs = betPotentialInputs.value;
  if (!inputs.length) return [];

  return inputs.map((line) => {
    const potentialWin = calcPotentialWin(
      line.stake,
      line.odds,
      line.drawCount ?? 1,
      line.numBets ?? 1
    );
    if (unref(payoutMaxPerBetslip)) {
      return potentialWin;
    }
    return displayPotentialWin(
      line.stake,
      line.odds,
      unref(payoutMax),
      line.drawCount,
      line.numBets
    );
  });
});

const applyNormalizedStake = (index: number, rawStake: number) => {
  if (!bets.value[index]) return;

  // Single-line promos lock the one stake to the face value. Multi-line
  // free bets keep per-line stakes (Y, Z, …) and must sum to the promo total.
  if (
    promotionStore.isAppliedOnSlip &&
    promotionStore.activePromotion?.singleLineOnly
  ) {
    rawStake = promotionStore.activePromotion.stake;
  }

  const bet = bets.value[index];
  const { drawCount, numBets } = getBetLineMultipliers(bet);
  const odds = toNumber(bet.odds);
  const stakeValue = normalizeStakeValue(rawStake, {
    ...getStakeNormalizeOptions(),
    odds,
    drawCount,
    numBets,
  });

  isNormalizingStakes.value = true;
  const updatedBets = [...bets.value];
  updatedBets[index] = { ...updatedBets[index], stake: stakeValue };
  betslipStore.betslip = updatedBets;
  betslipStore.saveToStorage();
  if (stakeValue > 0) {
    updateLastStake(stakeValue);
  }
  nextTick(() => {
    isNormalizingStakes.value = false;
  });
};

const removeBetAtIndex = (displayIndex: number) => {
  const betToRemove = bets.value[displayIndex];
  if (betToRemove) {
    iframeCommunication.sendMessage('bet-removed', { 
      bet: betToRemove,
      index: displayIndex 
    });
  }
  removeBet(displayIndex);
};

const closeBetslip = () => {
  emit('close');
};

const isEmpty = computed(() => {
  if (!bets.value || bets.value.length === 0) return true;
  return bets.value.some(
    (bet) => !Array.isArray(bet.draws) || bet.draws.length === 0 || !bet.stake || bet.stake <= 0
  );
});

const isAuthenticated = computed(() => authStore.isAuthenticated);

const hasAccountForBooking = computed(() => {
  const rawAccount = route.query.account;
  const fromRoute =
    typeof rawAccount === 'string'
      ? rawAccount
      : Array.isArray(rawAccount)
        ? rawAccount[0]
        : '';
  if (fromRoute && String(fromRoute).trim()) {
    return true;
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('account');
    if (fromUrl && fromUrl.trim()) {
      return true;
    }
  } catch {
    /* ignore */
  }
  const account =
    authStore.account ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_account') : null);
  return !!account && String(account).trim().length > 0;
});

const hasCurrencyForGameSettings = computed(() => {
  if (gameStore.getBetSettings?.currency_code) {
    return true;
  }
  const raw = route.query.currency;
  const fromRoute =
    typeof raw === 'string'
      ? raw
      : Array.isArray(raw)
        ? raw[0]
        : '';
  if (fromRoute && String(fromRoute).trim()) {
    return true;
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('currency');
    if (fromUrl && fromUrl.trim()) {
      return true;
    }
  } catch {
    /* ignore */
  }
  const c =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_currency') : null;
  return !!c && String(c).trim().length > 0;
});

const showBookABetModule = computed(() => {
  if (promotionStore.isAppliedOnSlip) return false;
  const settings = gameStore.getBetSettings;
  const modules = settings?.enabled_modules;
  if (Array.isArray(modules)) {
    return modules.includes('book_a_bet');
  }
  if (!settings && hasAccountForBooking.value && hasCurrencyForGameSettings.value) {
    return true;
  }
  return false;
});

const showPromotionModule = computed(() =>
  promotionStore.isModuleEnabled(gameStore.getBetSettings?.enabled_modules)
);

const showPromotionOffer = computed(
  () =>
    showPromotionModule.value &&
    isAuthenticated.value &&
    promotionLookupDone.value &&
    promotionStore.isActive
);

const promotionBannerText = computed(() => {
  const promo = promotionStore.activePromotion;
  if (!promo) return '';
  return promotionBannerLabel(promo);
});

const handlePromotionApplyToggle = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  promotionStore.setApplyToSlip(checked);
  if (!checked || !promotionStore.activePromotion) return;

  // Fill the face amount only when there is a single line. Do not copy it
  // onto every line — the promo covers the slip total (Y + Z + … = X).
  if (bets.value.length === 1) {
    applyNormalizedStake(0, promotionStore.activePromotion.stake);
  }
};

const resolveBetGameSlugs = (): string[] => {
  const detail = gameStore.getGameDetail;
  return bets.value
    .map((bet) => {
      if (detail?.slug && String(bet.game_id) === String(detail.id)) {
        return detail.slug.toLowerCase();
      }
      const name = String(bet.game_name || '').trim().toLowerCase();
      return name ? name.replace(/\s+/g, '-') : '';
    })
    .filter(Boolean);
};

/** Book-a-bet is only for a single game; online slip may hold multiple games for placing bets. */
const distinctGameIdCount = computed(() => {
  const ids = new Set<number>();
  for (const bet of bets.value) {
    const raw = bet?.game_id;
    const gid = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
    if (Number.isFinite(gid) && gid > 0) {
      ids.add(gid);
    }
  }
  return ids.size;
});

const distinctDrawKeyCount = computed(() => {
  const drawKeys = new Set<string>();
  for (const bet of bets.value) {
    const draws = Array.isArray(bet?.draws) ? bet.draws : [];
    for (const draw of draws) {
      const eventId =
        typeof draw?.event_id === 'number'
          ? draw.event_id
          : parseInt(String(draw?.event_id ?? ''), 10);
      if (Number.isFinite(eventId) && eventId > 0) {
        drawKeys.add(`event:${eventId}`);
        continue;
      }

      const drawDate = typeof draw?.draw_date === 'string' ? draw.draw_date.trim() : '';
      if (drawDate) {
        drawKeys.add(`date:${drawDate}`);
      }
    }
  }
  return drawKeys.size;
});

const singleGamePerTicketEnabled = computed(
  () =>
    gameStore.getBetSettings?.client_surface_ui?.online?.single_game_per_ticket ===
    true
);

const useOnlinePlatformPrintModal = computed(() => {
  const v = gameStore.getBetSettings?.client_surface_ui?.online?.use_platform_print_modal;
  return v !== false;
});

const hasMixedGamesForBookBet = computed(() => distinctGameIdCount.value > 1);

const hasMixedDrawsForBookBet = computed(() => distinctDrawKeyCount.value > 1);

const bookBetRestrictionMessage = computed(() => {
  if (!showBookABetModule.value) return '';
  if (hasMixedGamesForBookBet.value && hasMixedDrawsForBookBet.value) {
    return t('betslip.bookBetDifferentGamesAndDrawsDisabled');
  }
  if (hasMixedGamesForBookBet.value) {
    return singleGamePerTicketEnabled.value
      ? t('betslip.bookBetDifferentGamesDisabled')
      : t('betslip.bookBetDifferentGamesDisabled');
  }
  if (hasMixedDrawsForBookBet.value) {
    return t('betslip.bookBetDifferentDrawsDisabled');
  }
  return '';
});

const isBookBetDisabled = computed(() => {
  if (!showBookABetModule.value) return true;
  return Boolean(
    isProcessingBookBet.value ||
      isEmpty.value ||
      showExpiredBetOptions.value ||
      hasMixedGamesForBookBet.value ||
      hasMixedDrawsForBookBet.value ||
      exceedsBetslipPayoutMax.value
  );
});

const totalRawPotentialWin = computed(() => sumPotentialWins(betPotentialInputs.value));

const exceedsBetslipPayoutMax = computed(
  () =>
    unref(payoutMaxPerBetslip) &&
    unref(payoutMax) > 0 &&
    totalRawPotentialWin.value > unref(payoutMax)
);

const betNowPayoutMaxMessage = computed(() => {
  if (!exceedsBetslipPayoutMax.value) return "";
  return t("betslip.maxPossibleWinExceeds", {
    amount: formatCurrency(unref(payoutMax), unref(displayCurrency)),
  });
});

const totalStake = computed(() => {
  if (!bets.value) return 0;
  return bets.value.reduce((total, bet, index) => {
    const drawCount = Array.isArray(bet.draws) ? bet.draws.length : 1;
    const numBets = typeof bet.numBets === "number" && bet.numBets > 0 ? bet.numBets : 1;
    return total + effectiveStakeForBet(index) * numBets * drawCount;
  }, 0);
});

const totalReturns = computed(() => {
  if (!bets.value.length) return 0;
  if (unref(payoutMaxPerBetslip)) {
    return totalRawPotentialWin.value;
  }
  return lineDisplayPotentials.value.reduce((total, value) => total + value, 0);
});

const displayCurrency = computed((): string => {
  return unref(currencySymbol) || '';
});

/** ISO currency code for API / free-bet validation (not the display symbol). */
const sessionCurrencyCode = computed((): string => {
  const fromSettings = unref(currencyCode);
  if (fromSettings && String(fromSettings).trim()) {
    return String(fromSettings).trim();
  }
  const raw = route.query.currency;
  const fromRoute =
    typeof raw === 'string'
      ? raw
      : Array.isArray(raw)
        ? raw[0]
        : '';
  if (fromRoute && String(fromRoute).trim()) {
    return String(fromRoute).trim();
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('currency');
    if (fromUrl && fromUrl.trim()) {
      return fromUrl.trim();
    }
  } catch {
    /* ignore */
  }
  const fromSession =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_currency') : null;
  return fromSession && String(fromSession).trim() ? String(fromSession).trim() : '';
});

const promotionSlipValidationError = computed(() => {
  if (!promotionStore.isAppliedOnSlip || isEmpty.value) return '';
  return (
    promotionStore.validateSlip({
      lineCount: bets.value.length,
      totalStake: totalStake.value,
      currency: unref(sessionCurrencyCode),
      gameSlugs: resolveBetGameSlugs(),
    }) ?? ''
  );
});

let lastPromotionNotifyKey = '';
watch(
  () => [
    promotionStore.isAppliedOnSlip,
    bets.value.length,
    totalStake.value,
    promotionStore.activePromotion?.id,
  ],
  () => {
    if (!promotionStore.isAppliedOnSlip || isEmpty.value) {
      lastPromotionNotifyKey = '';
      return;
    }
    const err = promotionStore.validateSlip({
      lineCount: bets.value.length,
      totalStake: totalStake.value,
      currency: unref(sessionCurrencyCode),
      gameSlugs: resolveBetGameSlugs(),
    });
    if (err) return;
    const key = `${promotionStore.activePromotion?.id}:${bets.value.length}:${totalStake.value}`;
    if (key === lastPromotionNotifyKey) return;
    lastPromotionNotifyKey = key;
    promotionStore.notifyApplied({
      betCount: bets.value.length,
      gameSlugs: resolveBetGameSlugs(),
    });
  },
  { deep: true }
);

const formatCurrencyValue = (value: number) => {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatOddsValue = (value: number | string | undefined) => {
  if (value === undefined) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const useAltNameForMarket = computed(
  () => gameStore.getBetSettings?.client_surface_ui?.use_alt_name_for_market === true
);

const formatMarketDisplay = (bet: BetslipItem) => {
  const marketName = bet.market_name || t('betslip.standardBet');
  if (useAltNameForMarket.value) {
    return marketName;
  }

  let description = bet.market_description || '';
  
  if (description && bet.specifier) {
    description = description.replace('{value}', String(bet.specifier));
  }
  
  if (description) {
    return `${marketName}: ${description}`;
  }
  return marketName;
};

const focusBetslipOutcomeSection = (
  getTarget: () => HTMLElement | null | undefined,
  block: 'start' | 'end' | 'center' | 'nearest' = 'start'
) => {
  void scrollElementIntoViewAfterRender(getTarget, { block, offset: 16, focus: true });
};

const scrollToBottom = () => {
  const container = betslipContainer.value;
  if (!container) return;

  const scrollTargets: HTMLElement[] = [];
  const modalHost = container.closest('.mobile-betslip-content, .desktop-modal-box');
  if (modalHost instanceof HTMLElement) {
    scrollTargets.push(modalHost);
  }
  scrollTargets.push(container);

  for (const target of scrollTargets) {
    if (target.scrollHeight > target.clientHeight + 1) {
      target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' });
    }
  }
};

watch(
  betslip,
  async (newBets: BetslipItem[]) => {
    if (
      isNormalizingStakes.value ||
      isUpdatingFromProps.value ||
      isClearingBetslip.value ||
      !newBets ||
      !Array.isArray(newBets) ||
      newBets.length === 0
    ) {
      return;
    }
    await nextTick();

    let needsUpdate = false;
    const updatedBets = newBets.map((bet) => {
      const { drawCount, numBets } = getBetLineMultipliers(bet);
      const rawStake = bet.stake ?? unref(betDefault);
      const normalizedStake = normalizeStakeValue(rawStake, {
        ...getStakeNormalizeOptions(),
        odds: toNumber(bet.odds),
        drawCount,
        numBets,
      });
      if (normalizedStake !== bet.stake) {
        needsUpdate = true;
        return { ...bet, stake: normalizedStake };
      }
      return bet;
    });

    if (!needsUpdate) return;

    isNormalizingStakes.value = true;
    betslipStore.betslip = updatedBets;
    betslipStore.saveToStorage();
    nextTick(() => {
      isNormalizingStakes.value = false;
    });
  },
  { immediate: true, deep: true }
);

watch(() => bets.value.length, (newLength, oldLength) => {
  if (newLength > oldLength && newLength > 0) scrollToBottom();
});

watch(bookBetSuccessData, (data) => {
  if (data) {
    focusBetslipOutcomeSection(() => bookedBetSuccessRef.value);
  }
});

watch(showSuccess, (isVisible) => {
  if (isVisible) {
    focusBetslipOutcomeSection(() => betslipSuccessRef.value);
  }
});

const handleStakeKeydown = (event: KeyboardEvent) => {
  if (event.key === ',') event.preventDefault();
};

const endStakeEditing = (index: number) => {
  const next = { ...stakeEditingByIndex.value };
  delete next[index];
  stakeEditingByIndex.value = next;
  delete localStakeValues.value[index];
};

const commitStakeInput = (index: number, rawInput: string) => {
  const allowDec = unref(allowDecimals);
  const parsed = parseCommittedStakeInput(rawInput, allowDec);
  if (parsed == null) {
    setRawBetStake(index, 0);
    return;
  }
  applyNormalizedStake(index, parsed);
};

const flushPendingStakeEdits = () => {
  const editingIndices = Object.keys(stakeEditingByIndex.value).map(Number);
  for (const index of editingIndices) {
    if (!Number.isFinite(index)) continue;
    commitStakeInput(index, localStakeValues.value[index] ?? "");
    endStakeEditing(index);
  }
};

const handleStakeFocus = (_event: FocusEvent, index: number) => {
  stakeEditingByIndex.value = { ...stakeEditingByIndex.value, [index]: true };
  const bet = bets.value[index];
  const stake = bet?.stake ?? 0;
  localStakeValues.value[index] =
    stake > 0 ? formatStakeInputValue(stake) : "";
};

const handleStakeBlur = (event: FocusEvent, index: number) => {
  const input = event.target as HTMLInputElement;
  commitStakeInput(index, input.value);
  endStakeEditing(index);
};

const handleStakeChange = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement;
  const value = sanitizeStakeInputString(input.value, unref(allowDecimals));
  localStakeValues.value[index] = value;

  if (value === "" || value === ".") {
    setRawBetStake(index, 0);
    return;
  }

  const previewStake = parseStakePreviewAmount(value);
  if (previewStake > 0) {
    setRawBetStake(index, previewStake);
  } else {
    setRawBetStake(index, 0);
  }
};

const validateBetLimitsBeforeSubmit = (): string | null => {
  const maxPerLine = unref(betMax);
  const minPerLine = unref(betMin);
  const maxPayout = unref(payoutMax);
  const maxTotalStake = unref(betLimit);
  const applyPayoutMaxToBetslip = unref(payoutMaxPerBetslip);

  for (const bet of bets.value) {
    const stake = toNumber(bet.stake);
    const { drawCount, numBets } = getBetLineMultipliers(bet);

    if (minPerLine > 0 && stake < minPerLine) {
      return `Bet amount is below the minimum limit.`;
    }
    if (maxPerLine > 0 && stake > maxPerLine) {
      return `Bet amount exceeds the maximum limit. Maximum allowed bet per selection is ${maxPerLine}.`;
    }

    if (!applyPayoutMaxToBetslip && maxPayout > 0) {
      const odds = toNumber(bet.odds);
      if (odds > 0) {
        const potentialWin = calcPotentialWin(stake, odds, drawCount, numBets);
        if (potentialWin > maxPayout) {
          return t('betslip.winningsMustBeSmallerThan', { amount: maxPayout });
        }
      }
    }
  }

  if (applyPayoutMaxToBetslip && maxPayout > 0) {
    const totalPotentialWin = sumPotentialWins(betPotentialInputs.value);
    if (totalPotentialWin > maxPayout) {
      return t('betslip.winningsMustBeSmallerThan', { amount: maxPayout });
    }
  }

  if (maxTotalStake > 0 && totalStake.value > maxTotalStake) {
    return t('betslip.totalStakeExceedsMax', { amount: maxTotalStake });
  }

  return null;
};

const hasSelectedNumbers = (numbers: any) => {
  if (!numbers) return false;
  if (Array.isArray(numbers)) {
    return numbers.some(group => Array.isArray(group) && group.length > 0);
  }
  return false;
};

const lastBetReference = ref<string | null>(null);

const handleBetButtonClick = () => {
  if (!isAuthenticated.value) {
    // Send login request to parent (works for both iframe and non-iframe)
    iframeCommunication.sendLoginRequest('bet-attempt');
    
    // If not in iframe, we can also emit an event or show a message
    if (!iframeCommunication.isRunningInIframe()) {
      // For non-iframe mode, show a toast or redirect to login
      showToast({
        title: t('toast.loginRequired'),
        message: t('toast.loginRequiredMsg'),
        type: 'info',
        duration: 3000
      });
    }
    return;
  }
  placeBet();
};

const placeBet = async () => {
  flushPendingStakeEdits();
  if (isProcessingBet.value || isEmpty.value || exceedsBetslipPayoutMax.value) return;
  if (!handleExpiredBets()) return;
  const maxBets = gameStore.getBetSettings?.payload_max || 2000;
  if (bets.value.length > maxBets) {
    showToast({
      title: t('betslip.maxBetsExceeded'),
      message: t('betslip.maxBetsExceededMsg', { count: bets.value.length, max: maxBets }),
      type: "error",
      duration: 4000,
    });
    return;
  }
  const invalidBets = bets.value.filter(b => !b.outcome_id || b.outcome_id === "");
  if (invalidBets.length > 0) {
    showSuccess.value = false;
    showToast({
      title: t('betslip.invalidBetConfig'),
      message: t('betslip.invalidBetConfigMsg'),
      type: "error",
      duration: 4000,
    });
    return;
  }
  const limitError = validateBetLimitsBeforeSubmit();
  if (limitError) {
    inlineError.value = limitError;
    showToast({ title: t("common.error"), message: limitError, type: "error", duration: 5000 });
    return;
  }
  const promotionError = promotionStore.isAppliedOnSlip
    ? promotionStore.validateSlip({
        lineCount: bets.value.length,
        totalStake: totalStake.value,
        currency: unref(sessionCurrencyCode),
        gameSlugs: resolveBetGameSlugs(),
      })
    : null;
  if (promotionError) {
    inlineError.value = promotionError;
    showToast({ title: t("common.error"), message: promotionError, type: "error", duration: 5000 });
    return;
  }
  inlineError.value = null;
  isProcessingBet.value = true;
  try {
    const payload = buildBackendPayload();
    const result = await transactionsStore.createBet(payload);
    if (result.status === "success") {
      const promotionPayload = promotionStore.getPlaceBetPayload();
      lastBetReference.value = result.bets_processed.reference;
      showSuccess.value = true;
      showToast({
        title: t('betslip.betPlacedSuccess'),
        message: t('betslip.goodLuck'),
        type: "success",
        duration: 3000,
      });
      const betPlacedPayload = {
        reference: result.bets_processed.reference,
        totalStake: totalStake.value,
        ...(promotionPayload
          ? {
              paidAmount: result.paid_amount ?? 0,
              discountAmount: result.discount_amount ?? totalStake.value,
              promotionType: promotionPayload.type,
              promotionId: result.promotion_id ?? promotionPayload.id,
            }
          : result.promotion
            ? {
                paidAmount: result.paid_amount ?? 0,
                discountAmount: result.discount_amount ?? totalStake.value,
                promotionType: result.promotion_type ?? result.promotion?.type,
                promotionId: result.promotion_id ?? result.promotion?.id,
              }
            : {}),
        ...(typeof result.booking_code === "string" && result.booking_code
          ? { bookingCode: result.booking_code }
          : {}),
        ...(result.bet_placed_event && typeof result.bet_placed_event === "object"
          ? result.bet_placed_event
          : {}),
      };
      iframeCommunication.notifyBetPlaced(betPlacedPayload);
      if (promotionPayload) {
        promotionStore.clear('consumed');
      }
      if (!useOnlinePlatformPrintModal.value) {
        let betsSnapshot: BetslipItem[] = [];
        try {
          betsSnapshot = JSON.parse(JSON.stringify(bets.value)) as BetslipItem[];
        } catch {
          betsSnapshot = [...bets.value];
        }
        const receiptPayload = {
          ...betPlacedPayload,
          bets: betsSnapshot,
        };
        iframeCommunication.sendMessage("bet-receipt-external", receiptPayload);
        try {
          window.dispatchEvent(
            new CustomEvent("numbers-online-bet-receipt", { detail: receiptPayload })
          );
        } catch {
          /* ignore */
        }
      }
      emit("clearedAfterSubmission");
      currentBookingCode.value = null;
      clearBetslip(false);
      focusBetslipOutcomeSection(() => betslipSuccessRef.value);
      await transactionsStore.fetchTransactions();
      setTimeout(() => { showSuccess.value = false; }, 3000);
      setTimeout(() => { emit("close"); }, 5000);
    } else {
      throw new Error("Failed to place bet");
    }
  } catch (error: any) {
    showSuccess.value = false;
    const errorMessage = error?.userMessage || t('betslip.failedToPlaceBet');
    inlineError.value = errorMessage;
    showToast({ title: t('common.error'), message: errorMessage, type: "error", duration: 5000 });
    setTimeout(() => { inlineError.value = null; }, 5000);
  } finally {
    isProcessingBet.value = false;
  }
};

const handleLastBetRebet = () =>
  applyRebetFromCurrentTransaction({
    onComplete: () => {
      isLastBetModalOpen.value = false;
    },
    openBetslip: false,
  });

const viewLastBet = async () => {
  if (!lastBetReference.value) return;
  try {
    isLoadingLastBet.value = true;
    await transactionsStore.fetchTransactionDetail(lastBetReference.value);
    isLastBetModalOpen.value = true;
  } catch (error) {
    showToast({
      title: t('common.error'),
      message: t('betslip.failedToLoadLastBet'),
      type: "error",
      duration: 4000,
    });
  } finally {
    isLoadingLastBet.value = false;
  }
};

const viewWagers = () => {
  emit("close");
  nextTick(() => router.push({ path: "/wagers", query: route.query }));
};

const handleBookBet = async () => {
  flushPendingStakeEdits();
  if (isEmpty.value || isProcessingBookBet.value || isBookBetDisabled.value) return;
  authStore.refreshAuthFromExternalSources();
  if (!hasAccountForBooking.value) {
    showToast({
      title: t('toast.accountRequired'),
      message: t('toast.accountRequiredBookMsg'),
      type: 'info',
      duration: 5000,
    });
    return;
  }
  if (!handleExpiredBets()) return;
  isProcessingBookBet.value = true;
  try {
    const currentBets = [...bets.value];
    const result = await transactionsStore.bookBet(buildBackendPayload());
    if (result?.status === "success") {
      currentBookingCode.value = result.booking_code || null;
      bookBetSuccessData.value = { booking_code: result.booking_code || "", barcode: result.barcode, expires_at: result.expires_at, total_bet_amount: result.total_bet_amount, bets: currentBets };
      emit("clearedAfterSubmission");
      clearBetslip(false);
      focusBetslipOutcomeSection(() => bookedBetSuccessRef.value);
      showToast({ title: t('toast.betBooked'), message: t('toast.betBookedMsg'), type: "success", duration: 3000 });
    } else {
      throw new Error(result?.message || "Failed to book bet");
    }
  } catch (err: any) {
    showToast({ title: t('common.error'), message: err?.userMessage || err?.message || t('toast.failedToBookBet'), type: "error", duration: 5000 });
  } finally {
    isProcessingBookBet.value = false;
  }
};

const loadBookedBet = async () => {
  const code = bookingCodeInput.value?.trim();
  if (!code || isLoadingBooking.value) return;
  authStore.refreshAuthFromExternalSources();
  if (!hasAccountForBooking.value) {
    showToast({
      title: t('toast.accountRequired'),
      message: t('toast.accountRequiredLoadMsg'),
      type: 'info',
      duration: 5000,
    });
    return;
  }
  bookingMessage.value = "";
  bookingErrorMessage.value = "";
  bookBetSuccessData.value = null;
  isLoadingBooking.value = true;
  try {
    const result = await transactionsStore.loadBookedBet(code);
    const loadedBets = Array.isArray(result?.bet_data?.bets) ? result.bet_data.bets : [];

    if (result?.status === "success" && loadedBets.length > 0) {
      currentBookingCode.value = result.booking_code || null;
      storeClearBetslip();
      for (const b of loadedBets) {
        const draws = Array.isArray(b.draws) ? b.draws : [];
        const allAvailableDraws = Array.isArray(b.allAvailableDraws) ? b.allAvailableDraws : draws;
        const drawCount =
          typeof b.drawCount === "number" && b.drawCount > 0 ? b.drawCount : Math.max(draws.length, 1);
        const numBets =
          typeof b.numBets === "number" && b.numBets > 0 ? b.numBets : 1;

        betslipStore.addBet({
          market_id: b.market_id,
          market_group_id: b.market_group_id ?? 0,
          game_id: b.game_id,
          game_name: b.game_name,
          draws,
          allAvailableDraws,
          drawCount,
          numbers: Array.isArray(b.numbers) ? b.numbers : [[], []],
          stake: Number(b.stake) || 0,
          betType: b.betType,
          maxStake: typeof b.maxStake === "number" ? b.maxStake : undefined,
          odds: typeof b.odds === "string" ? parseFloat(b.odds) : b.odds,
          numBets,
          picks: b.picks,
          outcome_id: String(b.outcome_id ?? ""),
          label: b.label,
          market_name: b.market_name,
          market_description: b.market_description,
          specifier: b.specifier ?? null,
          select_numbers: Boolean(b.select_numbers),
          currency_symbol: b.currency_symbol,
        });
      }
      bookingCodeInput.value = "";
      bookingMessage.value = result.draws_updated
        ? t('toast.bookedBetLoadedDrawUpdated')
        : t('toast.bookedBetLoaded');
      setTimeout(() => { bookingMessage.value = ""; }, 5000);
    } else {
      bookingErrorMessage.value = result?.message || t('toast.noBetDataReturned');
      setTimeout(() => { bookingErrorMessage.value = ""; }, 5000);
    }
  } catch (err: any) {
    bookingErrorMessage.value = err?.userMessage || err?.message || t('toast.failedToLoadBookedBet');
    setTimeout(() => { bookingErrorMessage.value = ""; }, 5000);
  } finally {
    isLoadingBooking.value = false;
  }
};

const downloadBookedBarcode = async () => {
  const d = bookBetSuccessData.value;
  if (!d) return;
  
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  
  const width = 320;
  const padding = 20;
  let height = 220;
  if (d.barcode) height += 70;
  
  canvas.width = width;
  canvas.height = height;
  
  // White background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);
  
  let y = padding;
  
  // Title
  ctx.fillStyle = "#333";
  ctx.font = "bold 16px Arial";
  ctx.textAlign = "center";
  ctx.fillText(t('betslip.receiptTitle'), width / 2, y + 10);
  y += 35;
  
  // Booking code
  ctx.fillStyle = "#2563EB";
  ctx.font = "bold 22px monospace";
  ctx.fillText(d.booking_code, width / 2, y + 10);
  y += 35;
  
  // Barcode
  if (d.barcode) {
    const img = new Image();
    img.src = `data:image/png;base64,${d.barcode}`;
    await new Promise<void>((resolve) => {
      img.onload = () => {
        const bw = width - padding * 2;
        const bh = 50;
        ctx.drawImage(img, padding, y, bw, bh);
        y += bh + 15;
        resolve();
      };
      img.onerror = () => resolve();
    });
  }
  
  // Divider
  ctx.strokeStyle = "#E5E5E5";
  ctx.beginPath();
  ctx.moveTo(padding, y);
  ctx.lineTo(width - padding, y);
  ctx.stroke();
  y += 15;
  
  // Details
  ctx.font = "12px Arial";
  ctx.textAlign = "left";
  
  const drawRow = (label: string, value: string) => {
    ctx.fillStyle = "#666";
    ctx.fillText(label, padding, y);
    ctx.fillStyle = "#333";
    ctx.textAlign = "right";
    ctx.fillText(value, width - padding, y);
    ctx.textAlign = "left";
    y += 20;
  };
  
  drawRow(t('betslip.receiptAmountLabel'), formatCurrency(d.total_bet_amount || 0, unref(displayCurrency)));
  drawRow(t('betslip.receiptBetsLabel'), String(d.bets?.length || 0));
  drawRow(t('betslip.receiptExpiresLabel'), formatDate(d.expires_at || ''));
  
  y += 10;
  
  // Footer
  ctx.fillStyle = "#999";
  ctx.font = "10px Arial";
  ctx.textAlign = "center";
  ctx.fillText(t('betslip.scanOrUseCode'), width / 2, y);
  
  // Download
  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = `booking-${d.booking_code}.png`;
  link.click();
};

const isDrawExpired = (draw: string | { draw_date: string; cutoff: string }) => {
  let cutoffTime = new Date(typeof draw === "string" ? draw : (draw?.cutoff || draw?.draw_date));
  return !isNaN(cutoffTime.getTime()) && cutoffTime.getTime() <= new Date().getTime();
};

const validateDraws = () => {
  return !bets.value.some(bet => Array.isArray(bet.draws) && bet.draws.some(d => isDrawExpired(d)));
};

const handleExpiredBets = () => {
  const expired: any[] = [];
  bets.value.forEach((bet, betIndex) => {
    if (Array.isArray(bet.draws)) {
      bet.draws.forEach((draw, drawIndex) => {
        if (isDrawExpired(draw)) expired.push({ betIndex, drawIndex, draw });
      });
    }
  });
  if (expired.length > 0) {
    expiredBets.value = expired;
    showExpiredBetOptions.value = true;
    return false;
  }
  return true;
};

const collapsedDrawsVisibleCount = (drawCount: number): number => {
  if (drawCount <= 1) return drawCount;
  if (drawCount === 2) return 1;
  return 2;
};

const betDrawsForDisplay = (
  draws: BetslipItem['draws'],
  betIndex: number,
): NonNullable<BetslipItem['draws']> => {
  const list = Array.isArray(draws) ? draws : [];
  if (expandedDraws.value[betIndex]) return list;
  return list.slice(0, collapsedDrawsVisibleCount(list.length));
};

const hiddenDrawsCount = (drawCount: number, betIndex: number): number => {
  if (expandedDraws.value[betIndex] || drawCount <= 1) return 0;
  return drawCount - collapsedDrawsVisibleCount(drawCount);
};

const toggleDrawsExpanded = (betIndex: number) => {
  expandedDraws.value = {
    ...expandedDraws.value,
    [betIndex]: !expandedDraws.value[betIndex],
  };
};

const removeDrawFromBet = (betIndex: number, drawIndex: number) => {
  const bet = bets.value[betIndex];
  if (bet && Array.isArray(bet.draws) && bet.draws.length > 1) {
    bet.draws.splice(drawIndex, 1);
    betslipStore.saveToStorage();
    emit('drawsChanged', [...bet.draws]);
  }
};

const removeExpiredBets = () => {
  const sorted = [...expiredBets.value].sort((a, b) => {
    if (b.betIndex !== a.betIndex) return b.betIndex - a.betIndex;
    return b.drawIndex - a.drawIndex;
  });
  sorted.forEach(({ betIndex, drawIndex }) => {
    const bet = bets.value[betIndex];
    if (bet && Array.isArray(bet.draws)) {
      bet.draws.splice(drawIndex, 1);
      if (bet.draws.length === 0) removeBetAtIndex(betIndex);
    }
  });
  showExpiredBetOptions.value = false;
  expiredBets.value = [];
};

const moveToNextDraw = () => {
  let hasMovedAny = false;
  let failedMoves = 0;

  // Filter to only process single draw bets (draws.length === 1)
  const singleDrawExpiredBets = expiredBets.value.filter(({ betIndex }) => {
    const bet = bets.value[betIndex];
    return bet && Array.isArray(bet.draws) && bet.draws.length === 1;
  });

  if (singleDrawExpiredBets.length === 0) {
    inlineError.value = t('betslip.singleDrawMoveOnly');
    setTimeout(() => inlineError.value = null, 5000);
    return;
  }

  // Process each expired bet
  const updatedBets = [...bets.value];
  
  singleDrawExpiredBets.forEach((expiredBet) => {
    const bet = updatedBets[expiredBet.betIndex];
    
    if (!bet || !bet.allAvailableDraws || bet.allAvailableDraws.length === 0) {
      failedMoves++;
      return;
    }

    // Filter out expired draws and get the next available draw
    const validDraws = bet.allAvailableDraws.filter(draw => !isDrawExpired(draw));
    
    if (validDraws.length === 0) {
      failedMoves++;
      return;
    }

    const nextDraw = validDraws[0];
    
    // Update the bet with the next draw
    updatedBets[expiredBet.betIndex] = {
      ...bet,
      draws: [nextDraw]
    };
    hasMovedAny = true;
  });

  if (hasMovedAny) {
    betslipStore.betslip = updatedBets;
    betslipStore.saveToStorage();
  }

  showExpiredBetOptions.value = false;
  expiredBets.value = [];

  if (hasMovedAny && failedMoves === 0) {
    showToast({
      title: t('common.success'),
      message: t('betslip.betsMovedToNextDraw'),
      type: 'success',
    });
  } else if (hasMovedAny && failedMoves > 0) {
    showToast({
      title: t('common.warning'),
      message: t('betslip.someBetsMovedWarning', { count: failedMoves }),
      type: 'info',
    });
  } else {
    inlineError.value = t('betslip.noNextDrawAvailable');
    setTimeout(() => inlineError.value = null, 5000);
  }
};

const hasExpiredDraws = (bet: any) => {
  return Array.isArray(bet.draws) && bet.draws.some((d: any) => isDrawExpired(d));
};

const hasAnyGameWithNextDraw = computed(() => {
  return expiredBets.value.some(({ betIndex }) => {
    const b = bets.value[betIndex];
    if (!(b && Array.isArray(b.draws) && b.draws.length === 1)) return false;
    
    // Check if there is an available draw in allAvailableDraws
    return b.allAvailableDraws && b.allAvailableDraws.some(draw => !isDrawExpired(draw));
  });
});

const cleanupExpiredDraws = () => {
  if (!betslip.value || !validateDraws()) return;
};

const formatDate = (date: string | undefined | null) => {
  if (!date) return "";
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const seconds = d.getSeconds().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const buildBackendPayload = () => {
  const bookingCode =
    typeof currentBookingCode.value === 'string' ? currentBookingCode.value.trim() : '';

  const betsPayload = bets.value.map((bet) => ({
    game_id: bet.game_id,
    market_id: bet.market_id,
    market_group_id: Number(bet.market_group_id),
    draws: Array.isArray(bet.draws) ? bet.draws : [],
    numbers: bet.numbers,
    picks: bet.picks,
    stake: bet.stake,
    outcome_id: bet.outcome_id,
  }));

  return {
    bets: betsPayload,
    total_bet_amount: totalStake.value,
    currency: unref(sessionCurrencyCode),
    ...(bookingCode ? { booking_code: bookingCode } : {}),
    ...((): Record<string, unknown> => {
      const promotion = promotionStore.getPlaceBetPayload()
      return promotion ? { promotion } : {}
    })(),
  };
};

const handlePlacePromotionEvent = () => {
  if (!promotionStore.isAppliedOnSlip || isEmpty.value || isProcessingBet.value) return;
  void placeBet();
};

onMounted(() => {
  isMobile.value = window.innerWidth <= 768;
  window.addEventListener('resize', () => isMobile.value = window.innerWidth <= 768);
  cleanupTimer.value = window.setInterval(cleanupExpiredDraws, 30000);
  window.addEventListener('bitville:place-promotion', handlePlacePromotionEvent);

  authStore.refreshAuthFromExternalSources();
  void (async () => {
    try {
      if (!gameStore.getBetSettings) {
        await gameStore.fetchGameSettings(false);
      }
      await refreshPromotions(undefined, { silent: true });
    } catch {
      /* optional */
    } finally {
      promotionLookupDone.value = true;
    }
  })();
});

onUnmounted(() => {
  if (cleanupTimer.value) clearInterval(cleanupTimer.value);
  window.removeEventListener('bitville:place-promotion', handlePlacePromotionEvent);
});
</script>

<style scoped>
.betslip {
  background: var(--colour-surface-cont-lowest);
  color: var(--colour-base-text-colour);
  width: 100%;
  box-sizing: border-box;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  padding: 16px;
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--betslip-panel-radius, 0);
  box-shadow: var(--betslip-panel-shadow, none);
}

.betslip-header {
  position: relative;
  margin: 0 0 16px;
  padding: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.betslip-header h3 {
  margin: 0;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 22.31px;
  font-weight: 700;
  color: var(--text-detail-body);
  text-align: left;
  line-height: 100%;
}

.header-line {
  width: 100%;
  height: 4.85px;
  background: var(--colour-orange-button);
  margin-top: 14px;
}

.bet-item {
  background: var(--betslip-item-bg, var(--surface-detail-muted));
  border-radius: var(--betslip-item-radius, 8px);
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid var(--border-detail-card);
  overflow: visible;
}

.bet-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.game-name {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 15px;
  font-weight: 700;
  line-height: 100%;
  color: var(--text-detail-body);
}

.bet-item-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bet-odds {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 700;
  line-height: 100%;
  color: var(--text-detail-body);
}

.trash-icon-svg {
  width: 14px;
  height: 16px;
}

.remove-bet-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-detail-body);
  display: flex;
  align-items: center;
}

.bet-item-details {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 13px;
  color: var(--text-detail-card-muted);
  line-height: 130%;
  font-weight: 400;
}

.draws-section {
  margin-bottom: 12px;
}

.draws-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.draws-label {
  font-weight: 500;
  color: var(--text-detail-body);
}

.toggle-draws-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--colour-orange-text-colour);
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.toggle-draws-btn:hover {
  background-color: var(--surface-detail-card);
}

.chevron-icon {
  width: 14px;
  height: 14px;
  transition: transform 0.2s;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.draws-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.draw-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background-color: var(--colour-surface-cont-lowest);
  border-radius: var(--betslip-subitem-radius, 6px);
  font-size: 12px;
}

.draw-item.expired {
  background-color: var(--betslip-expired-draw-bg, color-mix(in srgb, var(--colour-red-text-colour) 12%, transparent));
  border: 1px solid var(--betslip-expired-draw-border, color-mix(in srgb, var(--colour-red-text-colour) 30%, transparent));
}

.draw-item.expired .draw-date {
  color: var(--betslip-expired-draw-color, var(--colour-red-text-colour));
}

.draw-date {
  color: var(--colour-base-text-colour);
}

.remove-draw-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  color: var(--colour-secondary-text-colour);
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.remove-draw-btn:hover {
  background-color: color-mix(in srgb, var(--colour-red-text-colour) 10%, transparent);
  color: var(--colour-red-text-colour);
}

.remove-draw-btn svg {
  width: 14px;
  height: 14px;
}

.more-draws-indicator {
  font-size: 11px;
  color: var(--colour-secondary-text-colour);
  padding: 4px 10px;
  font-style: italic;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
}

.more-draws-indicator.clickable {
  cursor: pointer;
  color: var(--colour-orange-text-colour);
  font-style: normal;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.more-draws-indicator.clickable:hover {
  background-color: var(--surface-detail-card);
  text-decoration: underline;
}

.market-name, .bet-type {
  margin-bottom: 6px;
}

.numbers-section {
  margin-top: 16px;
  margin-bottom: 4px;
}

.numbers-label {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 13px;
  font-weight: 400;
  line-height: 100%;
  margin-bottom: 8px;
  color: var(--text-detail-body);
}

.number-circles {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.number-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--colour-surface-cont-lowest);
  border: 1.5px solid var(--colour-surface-cont-high);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: var(--colour-base-text-colour);
}

.number-circle.bonus {
  background: var(--colour-surface-cont-lowest);
  border-color: var(--colour-orange-button);
  color: var(--colour-orange-text-colour);
}

.plus-sign {
  font-size: 16px;
  font-weight: 700;
  align-self: center;
  color: var(--text-detail-body);
}

.bet-inputs-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  width: 100%;
  clear: both;
  position: relative;
  z-index: 0;
}

.input-fieldset {
  flex: 1;
  min-width: 0;
  min-height: 48px;
  position: relative;
  overflow: visible;
  border: 1px solid var(--colour-surface-cont-high);
  border-radius: var(--betslip-input-radius, 8px);
  padding: 12px 12px 8px;
  display: flex;
  align-items: center;
  background: var(--betslip-input-bg, var(--surface-detail-muted));
  box-sizing: border-box;
}

.input-fieldset.readonly {
  flex: 1;
  width: auto;
}

.input-fieldset-label {
  position: absolute;
  top: 0;
  left: 12px;
  right: auto;
  display: flex;
  justify-content: flex-start;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.input-fieldset label {
  position: static;
  transform: none;
  background: var(--betslip-input-bg, var(--surface-detail-muted));
  padding: 0 6px;
  font-size: 12px;
  color: var(--text-detail-body);
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-align: left;
  pointer-events: auto;
}

.input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0;
}

.input-wrapper input {
  width: 100%;
  border: none;
  background: transparent;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  color: var(--text-detail-body);
  padding: 0;
  outline: none;
}

.currency {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  color: var(--text-detail-body);
  flex-shrink: 0;
}

.win-value {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0;
  color: var(--text-detail-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.betslip-footer {
  margin-top: 16px;
}

.footer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}

.promotion-offer-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: min(100%, calc(100% - 100px));
  min-height: 28px;
  padding: 4px 10px;
  border-radius: var(--betslip-secondary-button-radius, 4px);
  border: 0.49px solid var(--colour-surface-cont);
  background: var(--colour-surface-cont-lowest);
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
}

.promotion-offer-inline-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  line-height: 1.2;
}

.promotion-offer-inline-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}

.promotion-offer-inline-detail {
  font-size: 10px;
  font-weight: 500;
  color: var(--colour-orange-button, #ff3304);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.promotion-offer-checkbox {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: var(--colour-orange-button, #ff3304);
}

.promotion-offer-error {
  margin: -8px 0 12px;
  font-size: 12px;
  line-height: 1.35;
  color: var(--colour-error, #dc2626);
  text-align: right;
}

.clear-all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6.8px;
  background: var(--colour-surface-cont-lowest);
  border: 0.49px solid var(--colour-surface-cont);
  border-radius: var(--betslip-secondary-button-radius, 4px);
  width: 90px;
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--colour-base-text-colour);
  box-sizing: border-box;
}

.trash-icon-small {
  width: 12px;
  height: 12px;
}

.summary-box {
  background: var(--betslip-summary-bg, var(--colour-surface-cont));
  border-radius: var(--betslip-summary-radius, 4px);
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.summary-label {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 13px;
  font-weight: 400;
  line-height: 100%;
  text-align: center;
  color: var(--colour-secondary-text-colour);
}

.summary-value {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 15px;
  font-weight: 700;
  line-height: 100%;
  text-align: center;
  color: var(--colour-base-text-colour);
}

.betslip-action-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
}

.betslip-action-buttons:has(.book-bet-btn) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.bet-now-btn {
  flex: 1;
  min-width: 0;
  height: 36px;
  background: var(--colour-green-button);
  color: var(--text-button);
  border: none;
  border-radius: var(--betslip-primary-button-radius, 6px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13.78px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
}

.betslip-action-buttons:not(:has(.book-bet-btn)) .bet-now-btn {
  width: 200px;
  flex: none;
}

.bet-now-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--colour-green-button) 88%, black 12%);
}

.bet-now-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.bet-now-btn.payout-max-exceeded {
  background: var(--colour-red-text-colour);
  color: var(--text-button);
  opacity: 1;
  cursor: not-allowed;
  font-size: 12px;
  font-weight: 600;
  padding: 0 12px;
}

.bet-now-btn.payout-max-exceeded:hover {
  background: var(--colour-red-text-colour);
}

.payout-max-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  text-align: center;
}

.payout-max-warning-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}


.book-bet-btn {
  flex: 1;
  min-width: 0;
  height: 36px;
  background: var(--colour-surface-cont-lowest);
  color: var(--bg-secondary-button-color, var(--colour-base-text-colour));
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--betslip-secondary-button-radius, 6px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13.78px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
}

.book-bet-btn:hover:not(:disabled) {
  background: var(--bg-hover, var(--colour-surface-cont-low));
  border-color: var(--colour-orange-button);
}

.book-bet-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.book-bet-btn.processing {
  opacity: 0.7;
}

.book-bet-restriction-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 12px;
  background: var(--book-bet-restriction-bg, color-mix(in srgb, var(--colour-yellow-text-colour) 10%, transparent));
  border: 1px solid var(--book-bet-restriction-border, color-mix(in srgb, var(--colour-yellow-text-colour) 45%, transparent));
  border-radius: 8px;
}

.book-bet-restriction-message .warning-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  color: var(--book-bet-restriction-color, var(--colour-base-text-colour));
  line-height: 1;
  margin-top: 1px;
}

.book-bet-restriction-message .info-icon-svg {
  display: block;
  width: 16px;
  height: 16px;
}

.book-bet-restriction-message .warning-text {
  font-size: 12px;
  line-height: 1.45;
  font-weight: 600;
  color: var(--book-bet-restriction-color, var(--colour-base-text-colour));
}

.terms-text {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 11px;
  font-weight: 400;
  line-height: 100%;
  text-align: center;
  color: var(--colour-secondary-text-colour);
  margin-top: 8px;
}

.expired-bet-options {
  background: color-mix(in srgb, var(--colour-red-text-colour) 12%, transparent);
  border: 1px solid var(--colour-red-text-colour);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.inline-error-message,
.booking-message.error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: color-mix(in srgb, var(--colour-red-text-colour) 12%, transparent);
  border: 1px solid var(--colour-red-text-colour);
  border-radius: 8px;
}

.inline-error-message .error-text,
.booking-message.error {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--colour-red-text-colour);
}

.bet-item.expired-bet {
  outline: 1px solid var(--colour-red-text-colour);
  outline-offset: -1px;
}

.expired-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  justify-content: center;
}

.warning-icon {
  font-size: 16px;
}

.warning-text {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 700;
  color: var(--colour-red-text-colour);
}

.expired-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.remove-expired-btn, .move-to-next-btn {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.remove-expired-btn {
  background: var(--expired-remove-button-bg, transparent);
  border: 1px solid var(--expired-remove-button-border, var(--colour-red-text-colour));
  color: var(--expired-remove-button-color, var(--colour-red-text-colour));
}

.remove-expired-btn:hover {
  background: var(--expired-remove-button-hover-bg, var(--colour-red-text-colour));
  color: var(--expired-remove-button-hover-color, var(--text-button));
}

.move-to-next-btn {
  background: var(--colour-surface-cont-high);
  border: 1px solid var(--colour-surface-cont-high);
  color: var(--text-button);
}

.move-to-next-btn:hover {
  background: var(--primary-hover);
}

.view-last-bet {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.view-bet-button {
  width: 100%;
  height: 36px;
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--betslip-secondary-button-radius, 6px);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 600;
  color: var(--colour-secondary-text-colour);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.view-bet-button:hover:not(:disabled) {
  background: var(--colour-surface-cont);
  border-color: var(--colour-surface-cont-high);
}

.view-bet-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.close-betslip-button {
  width: 100%;
  margin-top: 12px;
  padding: 12px;
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--betslip-secondary-button-radius, 8px);
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}

@media (min-width: 769px) {
  .close-betslip-button { display: none; }
}

.success-message {
  background: var(--colour-green-button);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid var(--bg-success-hover);
}

.success-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.success-text h3 {
  margin: 0;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-button);
}

.success-text p {
  margin: 4px 0 0;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  color: var(--text-button);
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--colour-secondary-text-colour);
  background: var(--betslip-empty-bg, transparent);
  border-radius: var(--betslip-empty-radius, 0);
}

.empty-state p {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 16px;
  color: var(--colour-secondary-text-colour);
  margin: 0;
}

.booked-bet-success-inline {
  padding: 16px;
  text-align: center;
}

.booking-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.check-icon {
  color: var(--colour-green-button);
  font-size: 18px;
  font-weight: bold;
}

.booking-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--colour-base-text-colour);
}

.booking-code-box {
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--betslip-secondary-button-radius, 6px);
  padding: 12px;
  margin-bottom: 12px;
}

.booking-code {
  font-family: monospace;
  font-size: 18px;
  font-weight: 700;
  color: var(--colour-orange-button);
  letter-spacing: 1px;
}

.barcode-box {
  background: var(--colour-surface-cont-lowest);
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

.barcode-img {
  width: 100%;
  height: auto;
  max-height: 50px;
  object-fit: contain;
  display: block;
}

.booking-info {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: var(--colour-base-text-colour);
}

.expires-text {
  color: var(--colour-yellow-text-colour);
}

.booking-actions {
  display: flex;
  gap: 8px;
}

.download-btn, .done-btn {
  flex: 1;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.download-btn {
  background: var(--colour-green-button);
  color: var(--text-button);
  border: none;
}

.done-btn {
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  color: var(--colour-base-text-colour);
}

.betslip-load-booking {
  margin-bottom: 16px;
}

.booking-search-container {
  display: flex;
  align-items: center;
  width: 100%;
  height: 54px;
  box-sizing: border-box;
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--results-filter-radius, 8px);
  overflow: hidden;
  background: var(--colour-surface-cont-lowest);
  box-shadow: var(--shadow-control);
}

.booking-search-container:focus-within {
  border-color: var(--colour-surface-cont);
  box-shadow: none;
}

.booking-search-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 100%;
  padding: 0 18px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--colour-base-text-colour);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 16px;
  font-weight: 500;
  box-sizing: border-box;
}

.booking-search-input::placeholder {
  color: var(--text-control-placeholder, var(--colour-secondary-text-colour));
  opacity: 1;
}

.booking-search-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 100%;
  padding: 0 12px;
  border: 0;
  border-left: 1px solid color-mix(in srgb, var(--colour-surface-cont) 88%, transparent);
  background: var(--colour-surface-cont-low) !important;
  color: var(--text-control-strong, var(--colour-text-inverse)) !important;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: none;
}

.booking-search-button:hover:not(:disabled) {
  background: var(--colour-surface-cont-high) !important;
}

.booking-search-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .input-wrapper input,
  .booking-search-input {
    font-size: 16px;
  }
}
</style>
